import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { UserNote } from '../users/types';
import { attachIds } from '../utils';

import {
  Lead, ShortLead,
} from './types';
import { toLead, toShortLeads } from './utils';

// Define a service using a base URL and expected endpoints
export const leadsApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    getLeads: build.query<CognitoAPIResponse<ShortLead[]>, {
      type?: string,
      search?: string
    }>({
      query(arg) {
        const { type, search } = arg;

        return {
          url: 'leads/query',
          params: { type, search },
          method: 'GET',
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'Leads' as const, id })), 'Leads']
        : ['Leads']),
      transformResponse(response: any) {
        return toShortLeads(response);
      },
    }),
    getLead: build.query<CognitoAPIResponse<Lead>, {
      type: string,
      leadId: string,
    }>({
      query(arg) {
        const { type, leadId } = arg;

        return {
          url: `leads/${type}/${leadId}`,
          method: 'GET',
        };
      },
      providesTags: (result) => [
        { type: 'Leads', id: result?.data.id },
      ],
      transformResponse(response: any) {
        return toLead(response);
      },
    }),
    newLead: build.mutation<any, {attributes: any}>({
      query(arg) {
        const { attributes } = arg;

        return {
          url: 'leads/new',
          body: attributes,
          method: 'POST',
        };
      },
      invalidatesTags: ['Leads'],
    }),
    updateLead: build.mutation<any, {
      type: string,
      leadId: string,
      newAttributes: any
    }>({
      query(arg) {
        const { type, leadId, newAttributes } = arg;

        return {
          url: `leads/${type}/${leadId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Leads', id: `${arg.type}#${arg.leadId}` }],
    }),
    convertLead: build.mutation<any, {
      type: string,
      leadId: string,
    }>({
      query(arg) {
        const { type, leadId } = arg;

        return {
          url: `leads/${type}/${leadId}/convert`,
          method: 'PUT',
        };
      },
      // Invalidates all queries associated with the leads and students lists
      // (since converting a lead results in a student entry being created)
      invalidatesTags: (result, error, arg) => [{ type: 'Leads', id: `${arg.type}#${arg.leadId}` }, 'Students'],
    }),
    deleteLead: build.mutation<any, {type: string, leadId: string}>({
      query(arg) {
        const { type, leadId } = arg;

        return {
          url: `leads/${type}/${leadId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Leads', id: `${arg?.type}#${arg?.leadId}` }],
    }),
    newLeadNote: build.mutation<any, {
      type: string,
      leadId: string,
      note: string,
      attributes?: {
        assignee?: string,
        dueDatetime?: string,
      }
    }>({
      query(arg) {
        const { type, leadId, note, attributes } = arg;

        return {
          url: `leads/${type}/${leadId}/notes/new`,
          body: {
            note,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['UserNotes'],
    }),
    getLeadNotes: build.query<CognitoAPIResponse<UserNote[]>, {
      type: string,
      leadId: string,
      active?: boolean,
    }>({
      query(arg) {
        const { type, leadId, active } = arg;

        return {
          url: `leads/${type}/${leadId}/notes/query`,
          params: { active },
          method: 'GET',
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'UserNotes' as const, id })), 'UserNotes']
        : ['UserNotes']),
      transformResponse(response: any) {
        return attachIds(response, (entry: UserNote) => `${entry.userId}#${entry.creationDatetime}`);
      },
    }),
    updateLeadNote: build.mutation<any, {
      type: string,
      leadId: string,
      creationDatetime: number,
      newAttributes: any
    }>({
      query(arg) {
        const { type, leadId, creationDatetime, newAttributes } = arg;

        return {
          url: `leads/${type}/${leadId}/notes/${creationDatetime}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Every user note should be invalidated if the due date of the note is updated, as
      // a note without a due date could gain a due date; in which case it should show
      // up in the follow up page
      invalidatesTags: (result, error, arg) => (arg.newAttributes.dueDatetime
        ? ['UserNotes']
        : [{ type: 'UserNotes', id: `${arg.leadId}#${arg.creationDatetime}` }]),
    }),
    deleteLeadNote: build.mutation<any, {
      type: string,
      leadId: string,
      creationDatetime: number,
    }>({
      query(arg) {
        const { type, leadId, creationDatetime } = arg;

        return {
          url: `leads/${type}/${leadId}/notes/${creationDatetime}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'UserNotes', id: `${arg.leadId}#${arg.creationDatetime}` },
      ],
    }),
  }),
});

export const {
  useGetLeadsQuery,
  useGetLeadQuery,
  useDeleteLeadMutation,
  useConvertLeadMutation,
  useUpdateLeadMutation,
  useNewLeadMutation,
  useGetLeadNotesQuery,
  useNewLeadNoteMutation,
  useUpdateLeadNoteMutation,
  useDeleteLeadNoteMutation,
} = leadsApi;
